import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { addDays, format, startOfDay } from 'date-fns';
import { useHandleSubmitWrongPolicyNoCDV } from 'forms/hooks';
import { isNilOrEmpty } from 'ramda-extension';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import ArrowRight from '@gef-ui/components/atoms/SvgIcon/Icons/ArrowRight';
import useControlNext from '@gef-ui/form-core/hooks/state/useControlNext';
import useFormFieldValue from '@gef-ui/form-core/hooks/state/useFormFieldValue';
import { setValue } from '@gef-ui/form-core/stream/transfer/control';
import Autocomplete from '@gef-ui/form-material/Autocomplete';
import SubmitButton from '@gef-ui/form-material/SubmitButton';
import TextField from '@gef-ui/form-material/TextField';
import { getLabelMessage } from '@gef-ui/localization/messages';

import { ComponentLoader } from 'components';
import PaginationRow from 'components/atoms/GefMui/PaginationRow';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import TableHasNoResults from 'components/atoms/GefMui/TableHasNoResults';
import { codelists } from 'constants/codelists';
import { MaxLengths } from 'constants/forms';
import { defaultDateFormatDateFns } from 'constants/general';
import { urls } from 'features/common/fetch/constants';
import { getFetchState } from 'features/common/fetch/selectors';
import { centralConsentNotFilledOption } from 'features/views/productionControl/constants';
import { fetchMyDocuments, loadMore, loadPage } from 'features/views/productionControl/myDocuments/actions';
import { FIELDS, MY_DOCUMENTS_FORM_NAME } from 'features/views/productionControl/myDocuments/form';
import {
	getClientsMyDocuments,
	getMyDocuments,
	getMyDocumentsTotalRows,
	getUserIsSuperiorAndSubordinate,
} from 'features/views/productionControl/selectors';
import {
	generateChipWithTooltipOrThreeDashes,
	generateEscalationAndDueDateValues,
	generateInfoCircleOutlineTooltip,
} from 'features/views/productionControl/utils';
import { setDetailViewed } from 'features/views/productionControlDetail/slice';
import { equalOrThreeDashes, getCodelistByName, getRoutePath } from 'utils';

export const MyDocuments = ({ showMyDocuments }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const next = useControlNext(MY_DOCUMENTS_FORM_NAME);

	const documentTypeCodelist = useSelector(getCodelistByName(codelists.documentType));
	const documentStateCodelist = useSelector(getCodelistByName(codelists.documentState));
	const documentCheckFinalStateAdminCodelist = useSelector(getCodelistByName(codelists.documentCheckFinalStateAdmin));
	const centralConsentOptions = useMemo(
		() => [centralConsentNotFilledOption, ...documentCheckFinalStateAdminCodelist],
		[documentCheckFinalStateAdminCodelist],
	);

	const myDocuments = useSelector(getMyDocuments);
	const hasRecords = !isNilOrEmpty(myDocuments);
	const fetchState = useSelector(getFetchState(urls.koprMyDocuments));

	const clients = useSelector(getClientsMyDocuments);
	const controlStatus = useFormFieldValue({ fieldName: FIELDS.controlStatus });
	const isControlStatusDocumentNotFoundInDA = controlStatus === '1';

	const userIsSuperiorAndSubordinate = useSelector(getUserIsSuperiorAndSubordinate);
	const myDocumentsTotalRows = useSelector(getMyDocumentsTotalRows);
	const userIsSuperiorAndSubordinateWithDocuments = userIsSuperiorAndSubordinate && myDocumentsTotalRows > 0;

	const handleRedirectToDetail = useCallback(
		(documentId) => (event) => {
			event.preventDefault();
			event.stopPropagation();
			dispatch(setDetailViewed(true));

			const { pathname, state } = getRoutePath('koprDetail', { documentId }, true);
			navigate(pathname, { state });
		},
		[dispatch, navigate],
	);

	const handleSearchMyDocuments = useCallback(async () => {
		dispatch(fetchMyDocuments());
	}, [dispatch]);

	const handleChangeControlStatus = useCallback(() => {
		next(setValue({ fieldName: FIELDS.centralConsent, value: null }));
	}, [next]);

	const handleSubmitWrongPolicyNoCDV = useHandleSubmitWrongPolicyNoCDV({ continueFn: handleSearchMyDocuments });

	const handleLoadMore = useCallback(() => {
		dispatch(loadMore());
	}, [dispatch]);

	const handlePaginationClick = useCallback(
		(e, page) => {
			dispatch(loadPage(page - 1));
		},
		[dispatch],
	);

	const body = (
		<Stack spacing={2}>
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<SectionHeader text={getLabelMessage('productionControl:myDocuments')} />
				<Stack spacing={2}>
					<Grid container>
						<Grid size={12}>
							<Grid container spacing={2}>
								<Grid size={11}>
									<Grid container spacing={2}>
										<Grid size={2}>
											<TextField
												size="small"
												fieldName={FIELDS.policyNumber}
												label={getLabelMessage('productionControl:policyNumber')}
												fullWidth
												slotProps={{
													htmlInput: {
														maxLength: MaxLengths.POLICY_NO,
													},
												}}
											/>
										</Grid>
										<Grid size={3}>
											<ComponentLoader fetchStorePath={`${urls.codeTable}.DOCUMENT_STATE`}>
												<Autocomplete
													size="small"
													fieldName={FIELDS.controlStatus}
													label={getLabelMessage('productionControl:controlStatus')}
													options={documentStateCodelist}
													labelPropName="shortDescription"
													onChange={handleChangeControlStatus}
												/>
											</ComponentLoader>
										</Grid>
										<Grid size={2}>
											<ComponentLoader
												fetchStorePath={`${urls.codeTable}.DOCUMENT_CHECK_FINAL_STATE_ADMIN`}
											>
												<Autocomplete
													size="small"
													fieldName={FIELDS.centralConsent}
													label={getLabelMessage('productionControl:centralConsent')}
													options={centralConsentOptions}
													labelPropName="description"
													disabled={!isControlStatusDocumentNotFoundInDA}
												/>
											</ComponentLoader>
										</Grid>
										<Grid size={3}>
											<ComponentLoader fetchStorePath={urls.koprClientsMyDocuments}>
												<Autocomplete
													size="small"
													fieldName={FIELDS.client}
													label={getLabelMessage('productionControl:client')}
													options={clients}
													labelPropName="description"
												/>
											</ComponentLoader>
										</Grid>
										<Grid size={2}>
											<ComponentLoader fetchStorePath={`${urls.codeTable}.DOCUMENT_TYPE`}>
												<Autocomplete
													size="small"
													fieldName={FIELDS.documentType}
													label={getLabelMessage('productionControl:documentType')}
													options={documentTypeCodelist}
													labelPropName="description"
												/>
											</ComponentLoader>
										</Grid>
									</Grid>
								</Grid>
								<Grid size={1}>
									<Grid
										container
										sx={{
											justifyContent: 'center',
										}}
									>
										<SubmitButton
											variant="contained"
											onClick={handleSearchMyDocuments}
											onClickError={handleSubmitWrongPolicyNoCDV}
											text={
												<Box sx={{ mt: 0.5 }}>
													<ArrowRight />
												</Box>
											}
											fullWidth
										/>
									</Grid>
								</Grid>
							</Grid>
						</Grid>
					</Grid>
					<Table sx={{ borderRadius: 2 }} size="small">
						<TableHead>
							<TableRow sx={{ height: '80px' }}>
								<TableCell>{getLabelMessage('productionControl:policyNumber')}</TableCell>
								<TableCell>{getLabelMessage('productionControl:client')}</TableCell>
								<TableCell>{getLabelMessage('productionControl:product')}</TableCell>
								<TableCell>{getLabelMessage('productionControl:documentType')}</TableCell>
								<TableCell>{getLabelMessage('productionControl:controlStatus')}</TableCell>
								<TableCell>{getLabelMessage('productionControl:statement')}</TableCell>
								<TableCell>
									<div>{getLabelMessage('productionControl:escalationStatus')}</div>
									<div>{getLabelMessage('productionControl:dueDate')}</div>
								</TableCell>
								<TableCell>
									<div>{getLabelMessage('productionControl:firstEscalationDate')}</div>
									<div>{getLabelMessage('productionControl:secondEscalationDate')}</div>
								</TableCell>
								<TableCell />
								<TableCell />
							</TableRow>
						</TableHead>
						<TableBody>
							{!hasRecords ? (
								<TableHasNoResults fetchState={fetchState} />
							) : (
								myDocuments.map(
									(
										{
											id,
											policyNo,
											policyHolder,
											product,
											documentStateText,
											documentType,
											documentTypeText,
											lastNotice,
											createDate,
											solutionStateId,
										},
										i,
									) => (
										<TableRow key={i}>
											<TableCell>{equalOrThreeDashes(policyNo)}</TableCell>
											<TableCell>{equalOrThreeDashes(policyHolder)}</TableCell>
											<TableCell>{equalOrThreeDashes(product)}</TableCell>
											<TableCell>
												{generateChipWithTooltipOrThreeDashes(documentType, documentTypeText)}
											</TableCell>
											<TableCell>{equalOrThreeDashes(documentStateText)}</TableCell>
											<TableCell sx={{ fontSize: '25px' }}>
												{lastNotice?.notice && generateInfoCircleOutlineTooltip(lastNotice)}
											</TableCell>
											<TableCell>
												<div>
													{
														generateEscalationAndDueDateValues({
															createDate,
															solutionStateId,
														})?.escalation
													}
												</div>
												<div>
													{
														generateEscalationAndDueDateValues({
															createDate,
															solutionStateId,
														})?.dueDate
													}
												</div>
											</TableCell>
											<TableCell>
												<div>
													{solutionStateId === 1
														? format(
																addDays(startOfDay(new Date(createDate)), 10),
																defaultDateFormatDateFns,
														  )
														: equalOrThreeDashes()}
												</div>
												<div>
													{solutionStateId === 1
														? format(
																addDays(startOfDay(new Date(createDate)), 15),
																defaultDateFormatDateFns,
														  )
														: equalOrThreeDashes()}
												</div>
											</TableCell>
											<TableCell
												sx={{ fontWeight: 'bold', color: 'primary.main' }}
												onClick={handleRedirectToDetail(id)}
											>
												<Box sx={{ cursor: 'pointer' }}>
													{getLabelMessage('productionControl:detail')}
												</Box>
											</TableCell>
											<TableCell />
										</TableRow>
									),
								)
							)}
						</TableBody>
						<PaginationRow
							fetchStorePath={urls.koprMyDocuments}
							onLoadNext={handleLoadMore}
							onPaginationClick={handlePaginationClick}
						/>
					</Table>
				</Stack>
			</Paper>
		</Stack>
	);

	return userIsSuperiorAndSubordinateWithDocuments ? showMyDocuments && body : body;
};

MyDocuments.propTypes = {
	showMyDocuments: PropTypes.func,
};

export default MyDocuments;
